/* Each Shopify block is one screen. State stays in memory for this page only. */
(() => {
  if (customElements.get('rozzi-quiz')) return;

  class RozziQuiz extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.steps = [...this.querySelectorAll('[data-quiz-step]')];
      this.answers = new Map();
      this.index = 0;
      this.timers = [];
      this.claiming = false;
      this.designMode = this.dataset.designMode === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.nav = this.querySelector('[data-quiz-nav]');
      this.backButton = this.querySelector('[data-back]');
      this.continueButton = this.querySelector('[data-continue]');
      const options = { signal: this.controller.signal };

      this.addEventListener('click', (event) => {
        const answer = event.target.closest('[data-answer]');
        if (answer && this.current.contains(answer)) this.selectAnswer(answer);
        if (event.target.closest('[data-back]')) this.back();
        if (event.target.closest('[data-continue]')) this.next();
      }, options);
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.steps.findIndex((step) => step.dataset.blockId === event.detail.blockId);
        if (index >= 0) this.show(index, false);
      }, options);
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(this)) this.disconnectedCallback();
      }, options);
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          this.claiming = false;
          this.continueButton.removeAttribute('aria-busy');
          this.show(this.index, false);
        }
      }, options);

      this.querySelectorAll('img').forEach((img) => {
        const loaded = () => { img.dataset.loaded = 'true'; };
        if (img.complete) loaded();
        else {
          img.addEventListener('load', loaded, { ...options, once: true });
          img.addEventListener('error', loaded, { ...options, once: true });
        }
      });
      this.prepareDates();
      this.prepareReviews();
      this.show(0, false);
    }

    disconnectedCallback() {
      this.clearTimers();
      this.controller?.abort();
      this.controller = null;
    }

    get current() { return this.steps[this.index]; }
    get finalScreen() { return this.index === this.steps.length - 1 || this.current?.dataset.type === 'offer'; }
    get selections() { return this.answers.get(this.current.dataset.blockId) || new Set(); }

    clearTimers() {
      this.timers?.forEach((timer) => window.clearTimeout(timer));
      this.timers = [];
    }

    later(callback, delay) {
      this.timers.push(window.setTimeout(callback, delay));
    }

    selectAnswer(button) {
      if (this.claiming) return;
      const value = button.dataset.answer;
      const selected = new Set(this.selections);
      if (this.current.dataset.type === 'multi_choice') {
        if (selected.has(value)) selected.delete(value);
        else {
          if (value === this.current.dataset.exclusive) selected.clear();
          else selected.delete(this.current.dataset.exclusive);
          selected.add(value);
        }
        this.answers.set(this.current.dataset.blockId, selected);
        this.updateAnswers();
        this.updateNavigation();
      } else {
        this.answers.set(this.current.dataset.blockId, new Set([value]));
        this.updateAnswers();
        this.next();
      }
    }

    updateAnswers() {
      this.current.querySelectorAll('[data-answer]').forEach((button) => {
        const selected = this.selections.has(button.dataset.answer);
        button.classList.toggle('option--selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
    }

    canContinue() {
      const hasAnswers = this.current.querySelector('[data-answer]');
      return !hasAnswers || this.selections.size > 0;
    }

    updateNavigation() {
      // The navigation sits outside the screen, so copy only that screen's CTA overrides.
      for (const [key, property] of Object.entries({
        buttonBackground: '--quiz-button-background',
        buttonText: '--quiz-button-text',
        buttonHoverBackground: '--quiz-button-hover-background',
        buttonHoverText: '--quiz-button-hover-text',
      })) {
        const value = this.current.dataset[key];
        if (value) this.nav.style.setProperty(property, value);
        else this.nav.style.removeProperty(property);
      }
      const type = this.current.dataset.type;
      const loader = type === 'loader';
      const single = type === 'intro' || type === 'single_choice';
      this.backButton.hidden = this.index === 0 || loader || type === 'offer';
      this.continueButton.hidden = (loader && !this.designMode) || (single && !this.finalScreen && !!this.current.querySelector('[data-answer]'));
      this.continueButton.disabled = !this.canContinue() || this.claiming;
      this.querySelector('[data-continue-label]').textContent = this.current.dataset.buttonLabel || 'Continue';
      this.nav.hidden = this.backButton.hidden && this.continueButton.hidden;
      this.querySelector('[data-nav-inner]').classList.toggle('step-nav__inner--solo', this.finalScreen);
      this.continueButton.classList.toggle('btn-cta--full', this.finalScreen);
      this.continueButton.classList.toggle('btn-cta--xl', this.finalScreen || type === 'results');
    }

    show(index, focus = true) {
      this.clearTimers();
      if (!this.steps.length) {
        this.nav.hidden = true;
        return;
      }
      this.index = Math.max(0, Math.min(index, this.steps.length - 1));
      this.steps.forEach((step, i) => { step.hidden = i !== this.index; });
      this.querySelector('[data-step-count]').textContent = this.index + 1;
      const t = this.steps.length > 1 ? this.index / (this.steps.length - 1) : 1;
      const progress = Math.round((1 - Math.pow(1 - t, 1.6)) * 100);
      this.querySelector('[data-quiz-progress]').setAttribute('aria-valuenow', progress);
      this.querySelector('[data-quiz-progress-fill]').style.width = `${progress}%`;
      this.current.querySelectorAll('[data-step-label]').forEach((el) => { el.textContent = (this.dataset.stepLabelTemplate || 'Step [current] of [total]').replaceAll('[current]', this.index + 1).replaceAll('[total]', this.steps.length); });
      this.updateAnswers();
      this.updateNavigation();
      if (focus) {
        const heading = this.current.querySelector('[data-step-heading], h1, h2');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }
        window.scrollTo({ top: 0, behavior: this.reducedMotion ? 'instant' : 'smooth' });
      }
      if (this.current.dataset.type === 'loader') this.startLoader();
      if (this.current.dataset.type === 'social_proof') this.startReviews();
    }

    back() {
      if (this.claiming) return;
      let index = this.index - 1;
      // Completed interstitials should not trap visitors when they go back.
      while (index >= 0 && this.steps[index].dataset.type === 'loader') index--;
      if (index >= 0) this.show(index);
    }

    next() {
      if (this.claiming || !this.canContinue()) return;
      if (this.finalScreen) {
        // Selecting/previewing a block must never navigate out of the theme editor.
        if (this.designMode) return;
        this.claiming = true;
        this.continueButton.setAttribute('aria-busy', 'true');
        this.updateNavigation();
        this.later(() => window.location.assign(this.dataset.destination || 'https://www.rozzi.co/products/unwind'), 500);
      } else this.show(this.index + 1);
    }

    startLoader() {
      const screen = this.current;
      const rows = [...screen.querySelectorAll('[data-checklist-row]')];
      const fill = screen.querySelector('[data-loader-fill]');
      const percent = screen.querySelector('[data-loader-percent]');
      const progress = screen.querySelector('[data-loader-progress]');
      const duration = this.reducedMotion ? 500 : Math.max(1000, Number(screen.dataset.duration) * 1000 || 5100);
      const start = performance.now();
      const render = (fraction) => {
        const pct = Math.min(100, Math.round(fraction * 100));
        if (fill) fill.style.width = `${pct}%`;
        if (percent) percent.textContent = pct;
        if (progress) progress.setAttribute('aria-valuenow', pct);
        const phase = Math.floor(fraction * (rows.length + 1));
        rows.forEach((row, i) => {
          row.hidden = phase < i;
          row.classList.toggle('is-done', phase > i);
          row.classList.toggle('is-spinning', phase === i);
        });
      };
      render(this.designMode ? 1 : 0);
      if (this.designMode) return;
      const tick = () => {
        const fraction = Math.min(1, (performance.now() - start) / duration);
        render(fraction);
        if (fraction < 1) this.later(tick, 40);
        else this.later(() => this.next(), 200);
      };
      this.later(tick, 40);
    }

    prepareDates() {
      this.steps.forEach((step) => {
        const date = new Date();
        date.setDate(date.getDate() + Number(step.dataset.projectionDays || 14));
        const text = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
        step.querySelectorAll('[data-projected-date]').forEach((el) => { el.textContent = text; });
        step.querySelectorAll('[data-date-copy]').forEach((el) => {
          const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
          while (walker.nextNode()) walker.currentNode.textContent = walker.currentNode.textContent.replaceAll('[date]', text);
        });
      });
    }

    prepareReviews() {
      this.querySelectorAll('.proof-carousel').forEach((carousel) => {
        const slides = [...carousel.querySelectorAll('.quiz-reviews__slide')];
        const pagination = carousel.querySelector('[data-review-pagination]');
        if (!pagination || !slides.length) return;
        pagination.replaceChildren();
        carousel.quizReviewIndex = 0;
        carousel.showReview = (index) => {
          carousel.quizReviewIndex = index;
          slides.forEach((slide, i) => { slide.hidden = i !== index; });
          [...pagination.children].forEach((button, i) => {
            button.classList.toggle('is-active', i === index);
            button.setAttribute('aria-pressed', String(i === index));
          });
        };
        slides.forEach((slide, i) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'quiz-review-dot';
          button.setAttribute('aria-label', (this.dataset.reviewLabel || 'Show review [number]').replaceAll('[number]', i + 1));
          button.addEventListener('click', () => carousel.showReview(i), { signal: this.controller.signal });
          pagination.append(button);
          slide.querySelectorAll('.proof-card__stars').forEach((stars) => {
            stars.setAttribute('role', 'img');
            stars.setAttribute('aria-label', this.dataset.reviewRatingLabel || '5 out of 5 stars');
            stars.querySelectorAll('svg').forEach((svg) => svg.setAttribute('aria-hidden', 'true'));
          });
        });
        carousel.showReview(0);
      });
    }

    startReviews() {
      const carousel = this.current.querySelector('.proof-carousel');
      const count = carousel?.querySelectorAll('.quiz-reviews__slide').length;
      if (!carousel?.showReview || count < 2 || this.reducedMotion || this.designMode) return;
      const tick = () => {
        if (!carousel.matches(':hover') && !carousel.contains(document.activeElement) && !document.hidden) {
          carousel.showReview((carousel.quizReviewIndex + 1) % count);
        }
        this.later(tick, 3500);
      };
      this.later(tick, 3500);
    }
  }
  customElements.define('rozzi-quiz', RozziQuiz);
})();
